import React from 'react';

import {ApiExploreRun} from 'app/components/AgentExplorer/types';
import * as routeUtils from 'app/utils/routeUtils';

export const RECENT_RUNS_QUERY_KEY = 'agent-recent-runs';

export interface PendingRun {
  runId: number;
  /** Deep link back to the feature, captured when the run was started from its map page. */
  url?: string;
}

export interface ApiRecentRun {
  id: number;
  featureId: number;
  status: ApiExploreRun['status'];
  startedAt: string;
  promptKey: string | null;
  // Concept(s) segmented by a SAM run; absent on LLM analysis runs.
  samPrompt?: string | string[] | null;
  sceneCount: number;
  featureName: string;
  lensUrl: string | null;
  // Set when the run is one property of a run plan's batch; null for a standalone run.
  agentWorkflowRunId: number | null;
  // The batch's own status, which a backend job settles a minute or so after its last property's.
  agentWorkflowRunStatus: ApiExploreRun['status'] | null;
  agentWorkflowId: number | null;
  agentWorkflowTitle: string | null;
}

/** A run plan's batch, tracked as one unit however many properties it covers. */
export interface PendingBulkRun {
  workflowRunId: number;
  /** The plan's title, so a batch that has yet to create its runs still has a name. */
  title?: string;
  /** Deep link to the batch's own page, where every property's results land. */
  url?: string;
}

// Deep link into the run; the captured map URL beats the bare feature link.
export function pendingRunUrl(
  entry: PendingRun,
  run: ApiRecentRun | undefined
): string | undefined {
  const featureUrl = entry.url ?? run?.lensUrl ?? undefined;
  return featureUrl ? routeUtils.addAgentRunIdToUrl(featureUrl, entry.runId) : undefined;
}

export interface PendingAgentRunsContextValue {
  pendingRuns: ReadonlyMap<number, PendingRun>;
  addPendingRun: (run: PendingRun) => void;
  removePendingRun: (runId: number) => void;
  pendingBulkRuns: ReadonlyMap<number, PendingBulkRun>;
  /** Called at kickoff, before the batch has any runs of its own to be found in the poll. */
  addPendingBulkRun: (workflowId: number, workflowRunId: number, title?: string) => void;
  removePendingBulkRun: (workflowRunId: number) => void;
  /** Undefined until the first recent-runs response lands. */
  recentRuns: ApiRecentRun[] | undefined;
}

export const PendingAgentRunsContext = React.createContext<PendingAgentRunsContextValue | null>(
  null
);

export function usePendingAgentRuns(): PendingAgentRunsContextValue {
  const value = React.useContext(PendingAgentRunsContext);

  if (!value) {
    throw new Error('usePendingAgentRuns called without PendingAgentRunsProvider');
  }

  return value;
}
